import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useBrandingContext } from '@/contexts/BrandingContext';
import { Shield, Users, Loader2 } from 'lucide-react';
import cimfoniLogo from '@/assets/cimfoni_logo_transparent.png';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import PasswordStrengthIndicator, { isPasswordValid } from '@/components/PasswordStrengthIndicator';


const LoginForm = () => {
  const { organizationName, logoUrl, primaryColor } = useBrandingContext();
  const [currentView, setCurrentView] = useState<'signin' | 'signup' | 'demo'>('signin');
  const [loginCredentials, setLoginCredentials] = useState({
    email: '',
    password: ''
  });
  const [signUpCredentials, setSignUpCredentials] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    organizationName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [demoAccessCode, setDemoAccessCode] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [demoRole, setDemoRole] = useState<'superadmin' | 'manager' | 'staff'>('superadmin');
  const [accessCodeError, setAccessCodeError] = useState('');
  const { login, signUp } = useAuth();
  const { toast } = useToast();

  const demoLabels: Record<string, string> = {
    superadmin: 'Super Admin',
    manager: 'Manager',
    staff: 'Staff',
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccessCodeError('');
    setIsLoading(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase.functions.invoke('demo-login', {
        body: { accessCode: demoAccessCode, role: demoRole },
      });
      if (error || !data?.email || !data?.password) {
        setAccessCodeError(data?.error || error?.message || 'Invalid access code');
        return;
      }
      await handleQuickDemoLogin(data.email, data.password, demoLabels[demoRole] || demoRole);
    } catch (err: any) {
      setAccessCodeError(err?.message || 'Demo sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickDemoLogin = async (email: string, password: string, label: string) => {
    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);
    
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: result.error || 'Invalid login credentials'
      });
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await login(loginCredentials.email, loginCredentials.password);
    setIsLoading(false);
    
    if (result.mfaRequired) {
      // MFA pending — AuthContext sets mfaPending, Index.tsx will show MFAVerifyStep
      return;
    }
    
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: result.error || 'Invalid email or password'
      });
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid(signUpCredentials.password)) {
      toast({
        variant: "destructive",
        title: "Weak Password",
        description: "Password must meet all complexity requirements"
      });
      return;
    }

    if (signUpCredentials.password !== signUpCredentials.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password Mismatch",
        description: "Passwords do not match"
      });
      return;
    }

    if (!signUpCredentials.organizationName.trim()) {
      toast({
        variant: "destructive",
        title: "Organization Name Required",
        description: "Enter the organization name you want this account to manage."
      });
      return;
    }

    if (!consentChecked) {
      toast({
        variant: "destructive",
        title: "Consent Required",
        description: "You must agree to the Privacy Policy and Terms of Service"
      });
      return;
    }


    setIsLoading(true);
    const result = await signUp(
      signUpCredentials.email,
      signUpCredentials.password,
      signUpCredentials.fullName,
      signUpCredentials.fullName,
      signUpCredentials.organizationName || undefined
    );
    setIsLoading(false);

    if (result.success) {
      if (result.autoConfirmed) {
        // User is automatically signed in
        toast({
          title: "Account Created!",
          description: "Welcome! You're now signed in as a manager."
        });
      } else {
        // Email confirmation required
        toast({
          title: "Confirmation Email Sent",
          description: "Please check your email and click the confirmation link to activate your account."
        });
        setSignUpCredentials({ email: '', password: '', confirmPassword: '', fullName: '', organizationName: '' });
        setCurrentView('signin');
      }
    } else {
      toast({
        variant: "destructive",
        title: "Sign Up Failed",
        description: result.error || 'Failed to create account'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                  <div>
                    <img src={cimfoniLogo} alt="Cimfoni" className="h-16 w-16 object-contain" width={64} height={64} />
                  </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">{organizationName || 'Operations'}</div>
              </div>
            </div>

        {/* Login/Signup Card */}
        <Card className="shadow-xl border-0 bg-card/95 backdrop-blur">
          <CardContent className="pt-6">
            {/* Sign In View */}
            {currentView === 'signin' && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <h1 className="text-lg font-semibold">Sign In</h1>
                  <p className="text-sm text-muted-foreground">Access your account</p>
                </div>
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email Address</Label>
                    <Input 
                      id="login-email" 
                      type="email"
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="email"
                      placeholder="your@email.com" 
                      value={loginCredentials.email} 
                      onChange={e => setLoginCredentials(prev => ({
                        ...prev,
                        email: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground">Use the email address provided by your administrator</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input 
                      id="login-password" 
                      type="password"
                      autoComplete="current-password"
                      placeholder="Enter your password" 
                      value={loginCredentials.password} 
                      onChange={e => setLoginCredentials(prev => ({
                        ...prev,
                        password: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign In
                  </Button>
                  <div className="text-center">
                    <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                      Forgot Password?
                    </Link>
                  </div>
                </form>
                <div className="flex justify-center gap-4 pt-4 border-t">
                  <a
                    href="https://demo.cimfoni.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:underline"
                  >
                    Demo
                  </a>
                  <span className="text-sm text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={() => setCurrentView('signup')}
                    className="text-sm font-bold text-muted-foreground hover:underline"
                  >
                    New User
                  </button>
                </div>
              </div>
            )}

            {/* Sign Up View */}
            {currentView === 'signup' && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">First Time Setup</h3>
                  <p className="text-sm text-muted-foreground">Create your admin account</p>
                </div>
                <form onSubmit={handleSignUpSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-orgname">Organization Name</Label>
                    <Input 
                      id="signup-orgname" 
                      placeholder="Your shelter or agency name" 
                      value={signUpCredentials.organizationName} 
                      onChange={e => setSignUpCredentials(prev => ({
                        ...prev,
                        organizationName: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-fullname">Full Name</Label>
                    <Input 
                      id="signup-fullname" 
                      placeholder="John Doe" 
                      value={signUpCredentials.fullName} 
                      onChange={e => setSignUpCredentials(prev => ({
                        ...prev,
                        fullName: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input 
                      id="signup-email" 
                      type="email"
                      placeholder="your@email.com" 
                      value={signUpCredentials.email} 
                      onChange={e => setSignUpCredentials(prev => ({
                        ...prev,
                        email: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input 
                      id="signup-password" 
                      type="password" 
                      placeholder="Create a password" 
                      value={signUpCredentials.password} 
                      onChange={e => setSignUpCredentials(prev => ({
                        ...prev,
                        password: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                    <PasswordStrengthIndicator password={signUpCredentials.password} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">Confirm Password</Label>
                    <Input 
                      id="signup-confirm" 
                      type="password" 
                      placeholder="Confirm your password" 
                      value={signUpCredentials.confirmPassword} 
                      onChange={e => setSignUpCredentials(prev => ({
                        ...prev,
                        confirmPassword: e.target.value
                      }))} 
                      required 
                      disabled={isLoading}
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="consent"
                      checked={consentChecked}
                      onCheckedChange={(checked) => setConsentChecked(checked === true)}
                      disabled={isLoading}
                    />
                    <label htmlFor="consent" className="text-xs text-muted-foreground leading-tight cursor-pointer">
                      I agree to the{' '}
                      <Link to="/privacy-policy" className="text-primary hover:underline" target="_blank">Privacy Policy</Link>
                      {' '}and{' '}
                      <Link to="/terms-of-service" className="text-primary hover:underline" target="_blank">Terms of Service</Link>
                    </label>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading || !isPasswordValid(signUpCredentials.password) || !consentChecked}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Account
                  </Button>
                </form>
                <div className="flex justify-center gap-4 pt-4 border-t">
                  <a
                    href="https://demo.cimfoni.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:underline"
                  >
                    Demo
                  </a>
                  <span className="text-sm text-muted-foreground">|</span>
                  <button
                    type="button"
                    onClick={() => setCurrentView('signup')}
                    className={`text-sm font-bold hover:underline ${currentView === 'signup' ? 'font-semibold text-primary' : 'text-muted-foreground'}`}
                  >
                    New User
                  </button>
                </div>
              </div>
            )}

            {/* Demo View */}
            {currentView === 'demo' && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">Demo Mode</h3>
                  <p className="text-sm text-muted-foreground">Pick a role, enter the access code, and go</p>
                </div>

                <form onSubmit={handleDemoSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['superadmin', 'manager', 'staff'] as const).map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setDemoRole(role)}
                          className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs font-medium transition-colors ${
                            demoRole === role
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-input bg-background text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {role === 'superadmin' ? <Shield className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                          {demoLabels[role]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="demo-access-code">Access Code</Label>
                    <Input
                      id="demo-access-code"
                      type="password"
                      placeholder="Enter access code"
                      value={demoAccessCode}
                      onChange={e => {
                        setDemoAccessCode(e.target.value);
                        setAccessCodeError('');
                      }}
                      required
                      disabled={isLoading}
                    />
                    {accessCodeError && (
                      <p className="text-xs text-destructive">{accessCodeError}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Launch Demo
                  </Button>
                </form>

                <div className="flex justify-center pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentView('signin');
                      setDemoAccessCode('');
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Footer */}
        <div className="text-center space-y-1">
          <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms-of-service" className="hover:underline">Terms of Service</Link>
          </div>
          <p className="text-xs font-light text-muted-foreground">Mitch Solutions</p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
